import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BackButton, EditButton } from "../../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../../Components/Utils/Breadcrumb";
import { NormalHeading } from "../../../../../Components/Utils/Heading";
import axiosInstance from "../../../../../Services/axiosCommon";
import { GetAccess, GetRole } from "../../../../../Components/Utils/roles";

export default function ViewParticipantPage() {
  const { participantId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [data, setData] = useState(stateData || null);
  const [loading, setLoading] = useState(!stateData);

  useEffect(() => {
    if (!stateData && participantId) {
      setLoading(true);
      axiosInstance
        .get(`/meetings/participants/${participantId}`)
        .then((res) => {
          const p = res?.data?.data || res?.data;
          if (p) {
            setData(p);
            if (p.user_id && (!p.email || !p.phone)) {
              axiosInstance
                .get(`/user/user-details/${p.user_id}`)
                .then((uRes) => {
                  const u = uRes?.data?.data || uRes?.data;
                  if (u) {
                    const phoneStr =
                      typeof u.phone === "object" && u.phone !== null
                        ? u.phone.display || u.phone.number || ""
                        : u.phone || u.mobile || "";

                    setData((prev) => ({
                      ...prev,
                      name: prev?.name || prev?.user_name || u.name,
                      email: prev?.email || u.email,
                      phone: prev?.phone || phoneStr,
                      designation:
                        prev?.designation || u.designation || u.position,
                      department:
                        prev?.department || u.division || u.department,
                    }));
                  }
                })
                .catch(() => { });
            }
          }
        })
        .catch((error) => {
          console.error("Error fetching participant details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (stateData) {
      if (stateData.user_id && (!stateData.email || !stateData.phone)) {
        axiosInstance
          .get(`/user/user-details/${stateData.user_id}`)
          .then((uRes) => {
            const u = uRes?.data?.data || uRes?.data;
            if (u) {
              const phoneStr =
                typeof u.phone === "object" && u.phone !== null
                  ? u.phone.display || u.phone.number || ""
                  : u.phone || u.mobile || "";

              setData((prev) => ({
                ...prev,
                name: prev?.name || prev?.user_name || u.name,
                email: prev?.email || u.email,
                phone: prev?.phone || phoneStr,
                designation:
                  prev?.designation || u.designation || u.position,
                department:
                  prev?.department || u.division || u.department,
              }));
            }
          })
          .catch(() => { });
      }
    }
  }, [stateData, participantId]);

  const targetId = participantId || data?.id || data?.participant_id;

  const userName =
    data?.name ||
    data?.user_name ||
    data?.userName ||
    data?.user?.name ||
    (data?.user_id ? `User #${data.user_id}` : "-");

  return (
    <div className="w-full h-full py-2 px-4">
      {/* Header Bar */}
      <div className="flex justify-between items-center">
        <Breadcrumb
          items={[
            {
              name: "Meeting",
              link: "/meeting/participants",
            },
            {
              name: "Participants",
              link: "/meeting/participants",
            },
            {
              name: "View Participant",
            },
          ]}
        />
        <div className="flex gap-2 items-center">
          <Link
            to="/meeting/participants"
            state={{
              currentPage: pageIndex,
              searchQuery: searchQuery,
            }}
          >
            <BackButton link={true} />
          </Link>
          {(GetAccess("meeting_participants_edit") || GetRole()) && (
            <Link
              to={`/meeting/participants/edit/${targetId}`}
              state={{
                data: data,
                from: `/meeting/participants/view/${targetId}`,
                pageIndex: pageIndex,
                searchQuery: searchQuery,
              }}
            >
              <EditButton height="8" />
            </Link>
          )}
        </div>
      </div>

      {/* Main Details Card */}
      <div className="w-full mx-auto h-[calc(100vh-145px)] mt-4 bg-white rounded-xl overflow-y-scroll p-4">
        <NormalHeading title={"Participant Details"} />

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <span className="text-sm font-medium text-gray-500">
              Loading participant details...
            </span>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 mt-3">
            {/* Left Column */}
            <div className="w-full flex">
              <div className="w-full bg-white flex flex-col">
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Participant ID
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.id || data?.participant_id || "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Full Name
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5 font-semibold text-gray-800">
                    {userName}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Email
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.email || data?.user?.email || "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Phone
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.phone || data?.user?.phone || data?.mobile || "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Position / Designation
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.designation ||
                      data?.position ||
                      data?.user?.designation ||
                      "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Middle Divider */}
            <div className="hidden lg:block border-r border-[1px] border-slate-200/60 border-solid" />

            {/* Right Column */}
            <div className="w-full flex">
              <div className="w-full bg-white flex flex-col">
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Department / Division
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.department ||
                      data?.division ||
                      data?.user?.division ||
                      "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Meeting Role
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.meeting_role || data?.role || "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Status
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5">
                    {data?.status || "-"}
                  </p>
                </div>
                <div className="odd:bg-white even:bg-slate-50 w-full flex px-2">
                  <p className="w-1/2 py-2 text-xs font-medium text-[#111537]">
                    Special Notes
                  </p>
                  <p className="w-1/2 py-2 text-xs text-[#606279] pl-5 whitespace-pre-wrap">
                    {data?.special_notes ||
                      data?.notes ||
                      data?.comments ||
                      "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}